import { useState } from "react";
import { motion } from "framer-motion";
import { useMedia } from "@/context/MediaContext";
import { VideoGallery } from "@/components/home/Showcase";
import { CaseStory } from "@/components/home/WorkStories";
import { Lightbox } from "@/components/media/Lightbox";
import { CASE_STUDIES } from "@/content/site";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import PersonalCTA from "@/components/home/PersonalCTA";
import Contact from "@/components/site/Contact";
import InstagramReels from "@/components/media/InstagramReels";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";

const WorkPage = () => {
  const showcase = useMedia("showcase", 0);
  const library = useMedia("library");
  const cases = useMedia("case", 0);
  const [open, setOpen] = useState(null);
  const gallery = [...showcase.items, ...library.items.filter((m) => m.kind === "video")];

  return (
    <div data-testid="work-page">
      <Seo title="Our Work: Real Businesses, Filmed Properly" description="Vertical video, photography and stories from inside Australian food manufacturers, distributors, wholesalers and product businesses. No stock footage, no invented results." schema={[breadcrumbSchema([{ name: "Home", to: "/" }, { name: "Our Work", to: "/work" }])]} />
      <header className="pt-40 md:pt-52 pb-16 md:pb-24 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <Eyebrow>Our work</Eyebrow>
          <h1 className="mt-7 font-display uppercase text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.9] text-[#2E1B16] max-w-5xl">
            Real businesses, <span className="font-accent font-normal normal-case text-[#38BDF8]">filmed properly.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#2E1B16]/60 max-w-xl leading-relaxed">
            Factories, warehouses, kitchens, labs and the people who run them. Everything here was made for a real business with a real story behind it.
          </p>
        </motion.div>
      </header>

      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32" aria-labelledby="work-gallery-heading">
          <h2 id="work-gallery-heading" className="sr-only">Video gallery</h2>
          <VideoGallery items={gallery} onOpen={setOpen} testid="work-gallery" />
        </section>
      )}

      <InstagramReels />

      <section className="bg-[#F2EFE9] py-32 md:py-44" aria-labelledby="work-stories-heading">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>Stories</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 id="work-stories-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16]">The work behind the work.</h2>
          </Reveal>
          <div className="mt-20 md:mt-28 space-y-28 md:space-y-40">
            {CASE_STUDIES.map((s, i) => (
              <Reveal key={s.id} y={40}><CaseStory story={s} media={cases.items[i]} flip={i % 2 === 1} onOpen={setOpen} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      <PersonalCTA />
      <Contact pageLabel="Work page" />
      <Lightbox item={open} onClose={() => setOpen(null)} />
    </div>
  );
};

export default WorkPage;
